//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Control used to display a list of the errors.
//
//  Copyright:    © 2021 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   28.06.23 Sean Flook          WI40256 Changed Extent to Provenance where appropriate.
//    003   06.10.23 Sean Flook                  Added OneScotland record types. Use colour variables.
//    004   03.11.23 Sean Flook                  Added hyphen to one-way.
//    005   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and renamed successor to successorCrossRef.
//    006   14.12.23 Sean Flook                  Corrected note record type.
//    007   05.01.24 Sean Flook                  use CSS shortcuts.
//    008   08.03.24 Sean Flook        IMANN-348 Updated method to see if a street or property has changed and added in the missing Scottish records.
//    009   12.06.24 Sean Flook        IMANN-515 Use a List to display the errors and display each error as a separate item.
//    010   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//    011   27.06.24 Joel Benford      IMANN-685 OWE sequence numbers -> seqNum
//    012   22.07.24 Sean Flook        IMANN-766 Replace holding character ¬ with a comma.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    013   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    014   14.03.25 Sean Flook       IMANN-1696 Added ability to copy the errors to the clipboard.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    015   05.08.25 Sean Flook       IMANN-1932 Changes to prevent save dialog from appearing when not required.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    016   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    017   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import useSandboxStore from "../context/SandboxStore";

import PropertyContext from "../context/propertyContext";
import StreetContext from "../context/streetContext";
import LookupContext from "../context/lookupContext";
import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";

import { GetNewStreetData } from "../utils/StreetUtils";
import { GetNewPropertyData, getBilingualSource } from "../utils/PropertyUtils";
import { copyTextToClipboard, FormatDateTime, UpdateSandbox } from "./../utils/HelperUtils";
import ObjectComparison, {
  StreetComparison,
  PropertyComparison,
  streetDescriptorKeysToIgnore,
  esuKeysToIgnore,
  highwayDedicationKeysToIgnore,
  oneWayExemptionKeysToIgnore,
  successorCrossRefKeysToIgnore,
  maintenanceResponsibilityKeysToIgnore,
  reinstatementCategoryKeysToIgnore,
  specialDesignationKeysToIgnore,
  interestKeysToIgnore,
  constructionKeysToIgnore,
  heightWidthWeightKeysToIgnore,
  publicRightOfWayKeysToIgnore,
  noteKeysToIgnore,
  lpiKeysToIgnore,
  blpuAppCrossRefKeysToIgnore,
  provenanceKeysToIgnore,
  classificationKeysToIgnore,
  organisationKeysToIgnore,
} from "../utils/ObjectComparison";

import {
  Grid,
  Typography,
  IconButton,
  Link,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Stack,
  Box,
} from "@mui/material";
import { useEditConfirmation } from "../pages/EditConfirmationPage";

import CloseIcon from "@mui/icons-material/Close";
import { CopyIcon } from "./../utils/ADSIcons";

import { adsRed, adsDarkGrey, adsLightGreyB, adsOffWhite, adsLightGreyA50 } from "../utils/ADSColours";
import { ActionIconStyle, GetAlertStyle, GetAlertIcon, GetAlertSeverity } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import MessageDialog from "./../dialogs/MessageDialog";

ADSErrorList.propTypes = {
  onClose: PropTypes.func.isRequired,
};

function ADSErrorList({ onClose }) {
  const theme = useTheme();

  const sandboxStore = useSandboxStore((state) => state);
  const sourceStreet = useSandboxStore((state) => state.sourceStreet);
  const currentStreet = useSandboxStore((state) => state.currentStreet);
  const currentEsuRecord = useSandboxStore((state) => state.currentEsuRecord);
  const currentDescriptorRecord = useSandboxStore((state) => state.currentDescriptorRecord);
  const currentOneWayExemptionRecord = useSandboxStore((state) => state.currentOneWayExemptionRecord);
  const currentHighwayDedicationRecord = useSandboxStore((state) => state.currentHighwayDedicationRecord);
  const currentMaintenanceResponsibilityRecord = useSandboxStore(
    (state) => state.currentMaintenanceResponsibilityRecord
  );
  const currentReinstatementCategoryRecord = useSandboxStore((state) => state.currentReinstatementCategoryRecord);
  const currentOSSpecialDesignationRecord = useSandboxStore((state) => state.currentOSSpecialDesignationRecord);
  const currentInterestRecord = useSandboxStore((state) => state.currentInterestRecord);
  const currentConstructionRecord = useSandboxStore((state) => state.currentConstructionRecord);
  const currentSpecialDesignationRecord = useSandboxStore((state) => state.currentSpecialDesignationRecord);
  const currentHWWRecord = useSandboxStore((state) => state.currentHWWRecord);
  const currentPRoWRecord = useSandboxStore((state) => state.currentPRoWRecord);
  const currentStreetNoteRecord = useSandboxStore((state) => state.currentStreetNoteRecord);
  const sourceProperty = useSandboxStore((state) => state.sourceProperty);
  const currentProperty = useSandboxStore((state) => state.currentProperty);
  const currentLpiRecord = useSandboxStore((state) => state.currentLpiRecord);
  const currentProvenanceRecord = useSandboxStore((state) => state.currentProvenanceRecord);
  const currentSuccessorCrossRefRecord = useSandboxStore((state) => state.currentSuccessorCrossRefRecord);
  const currentPropertyNoteRecord = useSandboxStore((state) => state.currentPropertyNoteRecord);
  const currentAppCrossRefRecord = useSandboxStore((state) => state.currentAppCrossRefRecord);
  const currentClassificationRecord = useSandboxStore((state) => state.currentClassificationRecord);
  const currentOrganisationRecord = useSandboxStore((state) => state.currentOrganisationRecord);
  const setStore = useSandboxStore((state) => state.setStore);

  const propertyContext = useContext(PropertyContext);
  const streetContext = useContext(StreetContext);
  const lookupContext = useContext(LookupContext);
  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);

  const confirmDialog = useEditConfirmation(false);

  const [validationErrorOpen, setValidationErrorOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  const [hasASD, setHasASD] = useState(false);

  const getIssueStyle = {
    pl: theme.spacing(1),
    pr: theme.spacing(1),
    color: adsRed,
    fontWeight: 600,
    fontSize: "14px",
  };

  /**
   * Event to handle closing the dialog.
   *
   * @param {object} event The event object
   */
  const handleCloseClick = (event) => {
    event.stopPropagation();
    if (onClose) onClose();
  };

  /**
   * Event to handle copying the errors to the clipboard.
   *
   * @param {object} event The event object
   */
  const handleCopyClick = (event) => {
    event.stopPropagation();

    let copyString = `Copy of Validation Issue(s) from ${FormatDateTime(new Date())}\n\n`;

    if (streetContext.currentStreetHasErrors) {
      const streetTitle = `Street: ${streetContext.currentStreet.usrn} - ${streetContext.currentStreet.descriptor}`;
      copyString += `${streetTitle}\n`;
      copyString += `${"=".repeat(streetTitle.length)}\n\n`;

      if (streetContext.currentErrors.street.length > 0) {
        copyString += "Street errors\n";
        copyString += "-------------\n";
        streetContext.currentErrors.street.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.descriptor.length > 0) {
        copyString += "\nStreet Descriptor errors\n";
        copyString += "------------------------\n";
        streetContext.currentErrors.descriptor.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.esu.length > 0) {
        copyString += "\nESU errors\n";
        copyString += "----------\n";
        streetContext.currentErrors.esu.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.highwayDedication.length > 0) {
        copyString += "\nHighway Dedication errors\n";
        copyString += "-------------------------\n";
        streetContext.currentErrors.highwayDedication.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.oneWayExemption.length > 0) {
        copyString += "\nOne Way Exemption errors\n";
        copyString += "------------------------\n";
        streetContext.currentErrors.oneWayExemption.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.successorCrossRef.length > 0) {
        copyString += "\nSuccessor Cross Reference errors\n";
        copyString += "--------------------------------\n";
        streetContext.currentErrors.successorCrossRef.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.maintenanceResponsibility.length > 0) {
        copyString += "\nMaintenance Responsibility errors\n";
        copyString += "---------------------------------\n";
        streetContext.currentErrors.maintenanceResponsibility.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.reinstatementCategory.length > 0) {
        copyString += "\nReinstatement Category errors\n";
        copyString += "-----------------------------\n";
        streetContext.currentErrors.reinstatementCategory.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.osSpecialDesignation.length > 0) {
        copyString += "\nSpecial Designation errors\n";
        copyString += "--------------------------\n";
        streetContext.currentErrors.osSpecialDesignation.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.interest.length > 0) {
        copyString += "\nInterest errors\n";
        copyString += "---------------\n";
        streetContext.currentErrors.interest.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.construction.length > 0) {
        copyString += "\nConstruction errors\n";
        copyString += "-------------------\n";
        streetContext.currentErrors.construction.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.specialDesignation.length > 0) {
        copyString += "\nSpecial Designation errors\n";
        copyString += "--------------------------\n";
        streetContext.currentErrors.specialDesignation.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.heightWidthWeight.length > 0) {
        copyString += "\nHeight, Width and Weight errors\n";
        copyString += "-------------------------------\n";
        streetContext.currentErrors.heightWidthWeight.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.publicRightOfWay.length > 0) {
        copyString += "\nPublic Right of Way errors\n";
        copyString += "--------------------------\n";
        streetContext.currentErrors.publicRightOfWay.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (streetContext.currentErrors.note.length > 0) {
        copyString += "\nStreet Note errors\n";
        copyString += "------------------\n";
        streetContext.currentErrors.note.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }
    }

    if (propertyContext.currentPropertyHasErrors) {
      const propertyTitle = `Property: ${propertyContext.currentProperty.uprn} - ${propertyContext.currentProperty.address}`;
      copyString += `${propertyTitle}\n`;
      copyString += `${"=".repeat(propertyTitle.length)}\n\n`;

      if (propertyContext.currentErrors.blpu.length > 0) {
        copyString += "BLPU errors\n";
        copyString += "-----------\n";
        propertyContext.currentErrors.blpu.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.lpi.length > 0) {
        copyString += "\nLPI errors\n";
        copyString += "----------\n";
        propertyContext.currentErrors.lpi.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.provenance.length > 0) {
        copyString += "\nProvenance errors\n";
        copyString += "-----------------\n";
        propertyContext.currentErrors.provenance.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.crossRef.length > 0) {
        copyString += "\nCross Reference errors\n";
        copyString += "----------------------\n";
        propertyContext.currentErrors.crossRef.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.successorCrossRef.length > 0) {
        copyString += "\nSuccessor Cross Reference errors\n";
        copyString += "--------------------------------\n";
        propertyContext.currentErrors.successorCrossRef.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.organisation.length > 0) {
        copyString += "\nOrganisation errors\n";
        copyString += "-------------------\n";
        propertyContext.currentErrors.organisation.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.classification.length > 0) {
        copyString += "\nClassification errors\n";
        copyString += "---------------------\n";
        propertyContext.currentErrors.classification.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }

      if (propertyContext.currentErrors.note.length > 0) {
        copyString += "\nProperty Note errors\n";
        copyString += "--------------------\n";
        propertyContext.currentErrors.note.forEach((error) => {
          error.errors.forEach((errorText) => {
            copyString += `${error.field}: ${errorText.replace("¬", ",")}\n`;
          });
        });
      }
    }

    copyTextToClipboard(copyString);
    setMessageOpen(true);
  };

  /**
   * Event to handle when an error is clicked.
   *
   * @param {number} type The type of record with the error.
   * @param {object} error The error object.
   */
  const doErrorClick = (type, error) => {
    const streetTypes = [11, 13, 15, 16, 17, 51, 52, 53, 61, 62, 63, 64, 66, 72];
    const esuTypes = [16, 17];
    const propertyTypes = [21, 22, 23, 24, 30, 31, 32, 71];

    if (streetTypes.includes(type)) {
      if (type === 11) streetContext.onGoToField(type, undefined, null, error.field);
      else if (esuTypes.includes(type)) streetContext.onGoToField(type, error.index, error.esuIndex, error.field);
      else streetContext.onGoToField(type, error.index, null, error.field);
    } else if (propertyTypes.includes(type)) {
      if (type === 21) propertyContext.onGoToField(type, undefined, error.field);
      else propertyContext.onGoToField(type, error.index, error.field);
    }
  };

  /**
   * Method called when the user wants to keep any changes already done to the current record.
   */
  const doKeepChanges = () => {
    if (sourceStreet) {
      switch (streetContext.currentRecord.type) {
        case 13:
          updateEsuData();
          break;

        case 15:
          updateStreetDescriptorData();
          break;

        case 16:
          updateOneWayExemptionData();
          break;

        case 17:
          updateHighwayDedicationData();
          break;

        case 30:
          updateSuccessorCrossRefData();
          break;

        case 51:
          updateMaintenanceResponsibilityData();
          break;

        case 52:
          updateReinstatementCategoryData();
          break;

        case 53:
          updateOsSpecialDesignationData();
          break;

        case 61:
          updateInterestData();
          break;

        case 62:
          updateConstructionData();
          break;

        case 63:
          updateSpecialDesignationData();
          break;

        case 64:
          updateHwwData();
          break;

        case 66:
          updateProwData();
          break;

        case 72:
          updateStreetNoteData();
          break;

        default: // 11
          // No need to do anything as Street changes are sent to currentStreet already
          break;
      }
    } else if (sourceProperty) {
      switch (propertyContext.currentRecord.type) {
        case 22:
          updateProvenanceData();
          break;

        case 23:
          updateCrossRefData();
          break;

        case 24:
          updateLpiData();
          break;

        case 30:
          updateSuccessorCrossRefData();
          break;

        case 31:
          updateOrganisationData();
          break;

        case 32:
          updateClassificationData();
          break;

        case 71:
          updatePropertyNoteData();
          break;

        default: // 21
          // No need to do anything as BLPU changes are sent to currentProperty already
          break;
      }
    }
  };

  /**
   * Sets the associated street data for the current street and then clears the data from the sandbox.
   *
   * @param {array|null} esuData The ESU data for the street.
   * @param {array|null} successorCrossRefData The successor cross reference data for the street (OneScotland only).
   * @param {array|null} descriptorData The descriptor data for the street.
   * @param {array|null} noteData The note data for the street.
   * @param {array|null} maintenanceResponsibilityData The maintenance responsibility data for the street (OneScotland only).
   * @param {array|null} reinstatementCategoryData The reinstatement category data for the street (OneScotland only).
   * @param {array|null} osSpecialDesignationData The special designation data for the street (OneScotland only).
   * @param {array|null} interestData The interest data for the street (GeoPlace only).
   * @param {array|null} constructionData The construction data for the street (GeoPlace only).
   * @param {array|null} specialDesignationData The special designation data for the street (GeoPlace only).
   * @param {array|null} prowData The public rights of way data for the street (GeoPlace only).
   * @param {array|null} hwwData The height, width and weight restriction data for the street (GeoPlace only).
   * @param {string} clearType The type of data that we are clearing from the sandbox.
   */
  const setAssociatedStreetDataAndClear = (
    esuData,
    successorCrossRefData,
    descriptorData,
    noteData,
    maintenanceResponsibilityData,
    reinstatementCategoryData,
    osSpecialDesignationData,
    interestData,
    constructionData,
    specialDesignationData,
    prowData,
    hwwData,
    clearType
  ) => {
    const newStreetData = GetNewStreetData(
      currentStreet ? currentStreet : sourceStreet,
      esuData,
      successorCrossRefData,
      descriptorData,
      noteData,
      maintenanceResponsibilityData,
      reinstatementCategoryData,
      osSpecialDesignationData,
      interestData,
      constructionData,
      specialDesignationData,
      prowData,
      hwwData,
      settingsContext.isScottish,
      hasASD
    );

    setStore(UpdateSandbox(sandboxStore, "currentStreet", newStreetData, clearType));
  };

  /**
   * Sets the associated property data for the current property and then clears the data from the sandbox.
   *
   * @param {array|null} lpiData The LPI data for the property.
   * @param {array|null} provenanceData The provenance data for the property.
   * @param {array|null} crossRefData The cross reference data for the property.
   * @param {array|null} classificationData The classification data for the property (OneScotland only).
   * @param {array|null} organisationData The organisation data for the property (OneScotland only).
   * @param {array|null} successorCrossRefData The successor cross reference data for the property (OneScotland only).
   * @param {array|null} noteData The note data for the property.
   * @param {string} clearType The type of data that we are clearing from the sandbox.
   */
  const setAssociatedPropertyDataAndClear = (
    lpiData,
    provenanceData,
    crossRefData,
    classificationData,
    organisationData,
    successorCrossRefData,
    noteData,
    clearType
  ) => {
    const newPropertyData = GetNewPropertyData(
      settingsContext.isScottish,
      currentProperty ? currentProperty : sourceProperty,
      lpiData,
      provenanceData,
      crossRefData,
      classificationData,
      organisationData,
      successorCrossRefData,
      noteData
    );

    setStore(UpdateSandbox(sandboxStore, "currentProperty", newPropertyData, clearType));
  };

  /**
   * Event to update the ESU record with new data.
   *
   * @returns
   */
  const updateEsuData = () => {
    if (!currentEsuRecord) return;

    const newEsus = currentStreet.esus.map((x) => [currentEsuRecord].find((esu) => esu.pkId === x.pkId) || x);

    setAssociatedStreetDataAndClear(
      newEsus,
      currentStreet.successorCrossRefs,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      currentStreet.maintenanceResponsibilities,
      currentStreet.reinstatementCategories,
      currentStreet.osSpecialDesignations,
      currentStreet.interests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "esu"
    );
  };

  /**
   * Event to update the descriptor record with new data.
   *
   * @returns
   */
  const updateStreetDescriptorData = () => {
    if (!currentDescriptorRecord) return;

    const newStreetDescriptors = currentStreet.streetDescriptors.map(
      (x) => [currentDescriptorRecord].find((descriptor) => descriptor.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      currentStreet.successorCrossRefs,
      newStreetDescriptors,
      currentStreet.streetNotes,
      currentStreet.maintenanceResponsibilities,
      currentStreet.reinstatementCategories,
      currentStreet.osSpecialDesignations,
      currentStreet.interests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "descriptor"
    );
  };

  /**
   * Event to update the one-way exemption record with new data.
   *
   * @returns
   */
  const updateOneWayExemptionData = () => {
    if (!currentOneWayExemptionRecord) return;

    let newEsus = currentStreet.esus.filter((X) => X.esuId !== currentOneWayExemptionRecord.esuId);
    const existingEsu = currentStreet.esus.filter((X) => X.esuId === currentOneWayExemptionRecord.esuId);

    let newOneWayExemptions = existingEsu[0].oneWayExemptions.filter(
      (x) => x.pkId !== currentOneWayExemptionRecord.pkId
    );

    const updatedOneWayExemption = {
      changeType: currentOneWayExemptionRecord.changeType,
      oneWayExemptionType: currentOneWayExemptionRecord.oneWayExemptionType,
      recordEndDate: currentOneWayExemptionRecord.recordEndDate,
      oneWayExemptionStartDate: currentOneWayExemptionRecord.oneWayExemptionStartDate,
      oneWayExemptionEndDate: currentOneWayExemptionRecord.oneWayExemptionEndDate,
      oneWayExemptionStartTime: currentOneWayExemptionRecord.oneWayExemptionStartTime,
      oneWayExemptionEndTime: currentOneWayExemptionRecord.oneWayExemptionEndTime,
      oneWayExemptionPeriodicityCode: currentOneWayExemptionRecord.oneWayExemptionPeriodicityCode,
      pkId: currentOneWayExemptionRecord.pkId,
      esuId: currentOneWayExemptionRecord.esuId,
      seqNum: currentOneWayExemptionRecord.seqNum,
    };
    newOneWayExemptions.push(updatedOneWayExemption);

    const updatedEsu = {
      entryDate: currentEsuRecord ? currentEsuRecord.entryDate : existingEsu[0].entryDate,
      changeType: existingEsu[0].changeType,
      esuVersionNumber: existingEsu[0].esuVersionNumber,
      numCoordCount: existingEsu[0].numCoordCount,
      esuTolerance: currentEsuRecord ? currentEsuRecord.esuTolerance : existingEsu[0].esuTolerance,
      esuStartDate: currentEsuRecord ? currentEsuRecord.esuStartDate : existingEsu[0].esuStartDate,
      esuEndDate: currentEsuRecord ? currentEsuRecord.esuEndDate : existingEsu[0].esuEndDate,
      esuDirection: currentEsuRecord ? currentEsuRecord.esuDirection : existingEsu[0].esuDirection,
      wktGeometry: existingEsu[0].wktGeometry,
      pkId: existingEsu[0].pkId,
      esuId: existingEsu[0].esuId,
      highwayDedications: existingEsu[0].highwayDedications,
      oneWayExemptions: newOneWayExemptions,
    };
    newEsus.push(updatedEsu);

    const newStreetData = !hasASD
      ? {
          changeType: currentStreet.changeType,
          usrn: currentStreet.usrn,
          swaOrgRefNaming: currentStreet.swaOrgRefNaming,
          streetSurface: currentStreet.streetSurface,
          streetStartDate: currentStreet.streetStartDate,
          streetEndDate: currentStreet.streetEndDate,
          neverExport: currentStreet.neverExport,
          version: currentStreet.version,
          recordType: currentStreet.recordType,
          state: currentStreet.state,
          stateDate: currentStreet.stateDate,
          streetClassification: currentStreet.streetClassification,
          streetTolerance: currentStreet.streetTolerance,
          streetStartX: currentStreet.streetStartX,
          streetStartY: currentStreet.streetStartY,
          streetEndX: currentStreet.streetEndX,
          streetEndY: currentStreet.streetEndY,
          pkId: currentStreet.pkId,
          lastUpdateDate: currentStreet.lastUpdateDate,
          entryDate: currentStreet.entryDate,
          streetLastUpdated: currentStreet.streetLastUpdated,
          streetLastUser: currentStreet.streetLastUser,
          relatedPropertyCount: currentStreet.relatedPropertyCount,
          relatedStreetCount: currentStreet.relatedStreetCount,
          esus: newEsus,
          streetDescriptors: currentStreet.streetDescriptors,
          streetNotes: currentStreet.streetNotes,
        }
      : {
          changeType: currentStreet.changeType,
          usrn: currentStreet.usrn,
          swaOrgRefNaming: currentStreet.swaOrgRefNaming,
          streetSurface: currentStreet.streetSurface,
          streetStartDate: currentStreet.streetStartDate,
          streetEndDate: currentStreet.streetEndDate,
          neverExport: currentStreet.neverExport,
          version: currentStreet.version,
          recordType: currentStreet.recordType,
          state: currentStreet.state,
          stateDate: currentStreet.stateDate,
          streetClassification: currentStreet.streetClassification,
          streetTolerance: currentStreet.streetTolerance,
          streetStartX: currentStreet.streetStartX,
          streetStartY: currentStreet.streetStartY,
          streetEndX: currentStreet.streetEndX,
          streetEndY: currentStreet.streetEndY,
          pkId: currentStreet.pkId,
          lastUpdateDate: currentStreet.lastUpdateDate,
          entryDate: currentStreet.entryDate,
          streetLastUpdated: currentStreet.streetLastUpdated,
          streetLastUser: currentStreet.streetLastUser,
          relatedPropertyCount: currentStreet.relatedPropertyCount,
          relatedStreetCount: currentStreet.relatedStreetCount,
          esus: newEsus,
          streetDescriptors: currentStreet.streetDescriptors,
          streetNotes: currentStreet.streetNotes,
          interests: currentStreet.interests,
          constructions: currentStreet.constructions,
          specialDesignations: currentStreet.specialDesignations,
          publicRightOfWays: currentStreet.publicRightOfWays,
          heightWidthWeights: currentStreet.heightWidthWeights,
        };

    setStore(UpdateSandbox(sandboxStore, "currentStreet", newStreetData));
    setStore(UpdateSandbox(sandboxStore, "esu", updatedEsu));
  };

  /**
   * Event to update the highway dedication record with new data.
   *
   * @returns
   */
  const updateHighwayDedicationData = () => {
    if (!currentHighwayDedicationRecord) return;

    let newEsus = currentStreet.esus.filter((x) => x.esuId !== currentHighwayDedicationRecord.esuId);
    const existingEsu = currentStreet.esus.filter((x) => x.esuId === currentHighwayDedicationRecord.esuId);

    let newHighwayDedications = existingEsu[0].highwayDedications.filter(
      (x) => x.pkId !== currentHighwayDedicationRecord.pkId
    );

    const updatedHighwayDedication = {
      changeType: currentHighwayDedicationRecord.changeType,
      highwayDedicationCode: currentHighwayDedicationRecord.highwayDedicationCode,
      recordEndDate: currentHighwayDedicationRecord.recordEndDate,
      hdStartDate: currentHighwayDedicationRecord.hdStartDate,
      hdEndDate: currentHighwayDedicationRecord.hdEndDate,
      hdSeasonalStartDate: currentHighwayDedicationRecord.hdSeasonalStartDate,
      hdSeasonalEndDate: currentHighwayDedicationRecord.hdSeasonalEndDate,
      hdStartTime: currentHighwayDedicationRecord.hdStartTime,
      hdEndTime: currentHighwayDedicationRecord.hdEndTime,
      hdProw: currentHighwayDedicationRecord.hdProw,
      hdNcr: currentHighwayDedicationRecord.hdNcr,
      hdQuietRoute: currentHighwayDedicationRecord.hdQuietRoute,
      hdObstruction: currentHighwayDedicationRecord.hdObstruction,
      hdPlanningOrder: currentHighwayDedicationRecord.hdPlanningOrder,
      hdVehiclesProhibited: currentHighwayDedicationRecord.hdVehiclesProhibited,
      pkId: currentHighwayDedicationRecord.pkId,
      esuId: currentHighwayDedicationRecord.esuId,
      seqNum: currentHighwayDedicationRecord.seqNum,
    };
    newHighwayDedications.push(updatedHighwayDedication);

    const updatedEsu = {
      entryDate: currentEsuRecord ? currentEsuRecord.entryDate : existingEsu[0].entryDate,
      changeType: existingEsu[0].changeType,
      esuVersionNumber: existingEsu[0].esuVersionNumber,
      numCoordCount: existingEsu[0].numCoordCount,
      esuTolerance: currentEsuRecord ? currentEsuRecord.esuTolerance : existingEsu[0].esuTolerance,
      esuStartDate: currentEsuRecord ? currentEsuRecord.esuStartDate : existingEsu[0].esuStartDate,
      esuEndDate: currentEsuRecord ? currentEsuRecord.esuEndDate : existingEsu[0].esuEndDate,
      esuDirection: currentEsuRecord ? currentEsuRecord.esuDirection : existingEsu[0].esuDirection,
      wktGeometry: existingEsu[0].wktGeometry,
      pkId: existingEsu[0].pkId,
      esuId: existingEsu[0].esuId,
      highwayDedications: newHighwayDedications,
      oneWayExemptions: existingEsu[0].oneWayExemptions,
    };
    newEsus.push(updatedEsu);

    const newStreetData = !hasASD
      ? {
          changeType: currentStreet.changeType,
          usrn: currentStreet.usrn,
          swaOrgRefNaming: currentStreet.swaOrgRefNaming,
          streetSurface: currentStreet.streetSurface,
          streetStartDate: currentStreet.streetStartDate,
          streetEndDate: currentStreet.streetEndDate,
          neverExport: currentStreet.neverExport,
          version: currentStreet.version,
          recordType: currentStreet.recordType,
          state: currentStreet.state,
          stateDate: currentStreet.stateDate,
          streetClassification: currentStreet.streetClassification,
          streetTolerance: currentStreet.streetTolerance,
          streetStartX: currentStreet.streetStartX,
          streetStartY: currentStreet.streetStartY,
          streetEndX: currentStreet.streetEndX,
          streetEndY: currentStreet.streetEndY,
          pkId: currentStreet.pkId,
          lastUpdateDate: currentStreet.lastUpdateDate,
          entryDate: currentStreet.entryDate,
          streetLastUpdated: currentStreet.streetLastUpdated,
          streetLastUser: currentStreet.streetLastUser,
          relatedPropertyCount: currentStreet.relatedPropertyCount,
          relatedStreetCount: currentStreet.relatedStreetCount,
          esus: newEsus,
          streetDescriptors: currentStreet.streetDescriptors,
          streetNotes: currentStreet.streetNotes,
        }
      : {
          changeType: currentStreet.changeType,
          usrn: currentStreet.usrn,
          swaOrgRefNaming: currentStreet.swaOrgRefNaming,
          streetSurface: currentStreet.streetSurface,
          streetStartDate: currentStreet.streetStartDate,
          streetEndDate: currentStreet.streetEndDate,
          neverExport: currentStreet.neverExport,
          version: currentStreet.version,
          recordType: currentStreet.recordType,
          state: currentStreet.state,
          stateDate: currentStreet.stateDate,
          streetClassification: currentStreet.streetClassification,
          streetTolerance: currentStreet.streetTolerance,
          streetStartX: currentStreet.streetStartX,
          streetStartY: currentStreet.streetStartY,
          streetEndX: currentStreet.streetEndX,
          streetEndY: currentStreet.streetEndY,
          pkId: currentStreet.pkId,
          lastUpdateDate: currentStreet.lastUpdateDate,
          entryDate: currentStreet.entryDate,
          streetLastUpdated: currentStreet.streetLastUpdated,
          streetLastUser: currentStreet.streetLastUser,
          relatedPropertyCount: currentStreet.relatedPropertyCount,
          relatedStreetCount: currentStreet.relatedStreetCount,
          esus: newEsus,
          streetDescriptors: currentStreet.streetDescriptors,
          streetNotes: currentStreet.streetNotes,
          interests: currentStreet.interests,
          constructions: currentStreet.constructions,
          specialDesignations: currentStreet.specialDesignations,
          publicRightOfWays: currentStreet.publicRightOfWays,
          heightWidthWeights: currentStreet.heightWidthWeights,
        };

    setStore(UpdateSandbox(sandboxStore, "currentStreet", newStreetData));
    setStore(UpdateSandbox(sandboxStore, "esu", updatedEsu));
  };

  /**
   * Event to update the maintenance responsibility record with new data.
   *
   * @returns
   */
  const updateMaintenanceResponsibilityData = () => {
    if (!currentMaintenanceResponsibilityRecord) return;

    const newMaintenanceResponsibilities = currentStreet.maintenanceResponsibilities.map(
      (x) =>
        [currentMaintenanceResponsibilityRecord].find(
          (maintenanceResponsibility) => maintenanceResponsibility.pkId === x.pkId
        ) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      currentStreet.successorCrossRefs,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      newMaintenanceResponsibilities,
      currentStreet.reinstatementCategories,
      currentStreet.osSpecialDesignations,
      null,
      null,
      null,
      null,
      null,
      "maintenanceResponsibility"
    );
  };

  /**
   * Event to update the reinstatement category record with new data.
   *
   * @returns
   */
  const updateReinstatementCategoryData = () => {
    if (!currentReinstatementCategoryRecord) return;

    const newReinstatementCategories = currentStreet.reinstatementCategories.map(
      (x) =>
        [currentReinstatementCategoryRecord].find((reinstatementCategory) => reinstatementCategory.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      currentStreet.successorCrossRefs,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      currentStreet.maintenanceResponsibilities,
      newReinstatementCategories,
      currentStreet.osSpecialDesignations,
      null,
      null,
      null,
      null,
      null,
      "reinstatementCategory"
    );
  };

  /**
   * Event to update the special designation (OneScotland) record with new data.
   *
   * @returns
   */
  const updateOsSpecialDesignationData = () => {
    if (!currentOSSpecialDesignationRecord) return;

    const newSpecialDesignations = currentStreet.specialDesignations.map(
      (x) => [currentOSSpecialDesignationRecord].find((sd) => sd.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      currentStreet.successorCrossRefs,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      currentStreet.maintenanceResponsibilities,
      currentStreet.reinstatementCategories,
      newSpecialDesignations,
      null,
      null,
      null,
      null,
      null,
      "specialDesignations"
    );
  };

  /**
   * Event to update the interest record with new data.
   *
   * @returns
   */
  const updateInterestData = () => {
    if (!currentInterestRecord) return;

    const newInterests = currentStreet.interests.map(
      (x) => [currentInterestRecord].find((interest) => interest.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      null,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      null,
      null,
      null,
      newInterests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "interest"
    );
  };

  /**
   * Event to update the construction record with new data.
   *
   * @returns
   */
  const updateConstructionData = () => {
    if (!currentConstructionRecord) return;

    const newConstructions = currentStreet.constructions.map(
      (x) => [currentConstructionRecord].find((construction) => construction.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      null,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      null,
      null,
      null,
      currentStreet.interests,
      newConstructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "construction"
    );
  };

  /**
   * Event to update the special designation (GeoPlace) record with new data.
   *
   * @returns
   */
  const updateSpecialDesignationData = () => {
    if (!currentSpecialDesignationRecord) return;

    const newSpecialDesignations = currentStreet.specialDesignations.map(
      (x) => [currentSpecialDesignationRecord].find((sd) => sd.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      null,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      null,
      null,
      null,
      currentStreet.interests,
      currentStreet.constructions,
      newSpecialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "specialDesignation"
    );
  };

  /**
   * Event to update the height, width & weight record with new data.
   *
   * @returns
   */
  const updateHwwData = () => {
    if (!currentHWWRecord) return;

    const newHeightWidthWeights = currentStreet.heightWidthWeights.map(
      (x) => [currentHWWRecord].find((hww) => hww.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      null,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      null,
      null,
      null,
      currentStreet.interests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      newHeightWidthWeights,
      "hww"
    );
  };

  /**
   * Event to update the public rights of way record with new data.
   *
   * @returns
   */
  const updateProwData = () => {
    if (!currentPRoWRecord) return;

    const newPublicRightOfWays = currentStreet.publicRightOfWays.map(
      (x) => [currentPRoWRecord].find((prow) => prow.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      null,
      currentStreet.streetDescriptors,
      currentStreet.streetNotes,
      null,
      null,
      null,
      currentStreet.interests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      newPublicRightOfWays,
      currentStreet.heightWidthWeights,
      "prow"
    );
  };

  /**
   * Event to update the note (street) record with new data.
   *
   * @returns
   */
  const updateStreetNoteData = () => {
    if (!currentStreetNoteRecord) return;

    const newNotes = currentStreet.notes.map(
      (x) => [currentStreetNoteRecord].find((note) => note.pkId === x.pkId) || x
    );

    setAssociatedStreetDataAndClear(
      currentStreet.esus,
      currentStreet.successorCrossRefs,
      currentStreet.streetDescriptors,
      newNotes,
      currentStreet.maintenanceResponsibilities,
      currentStreet.reinstatementCategories,
      currentStreet.osSpecialDesignations,
      currentStreet.interests,
      currentStreet.constructions,
      currentStreet.specialDesignations,
      currentStreet.publicRightOfWays,
      currentStreet.heightWidthWeights,
      "streetNote"
    );
  };

  /**
   * Event to update the provenance record with new data.
   *
   * @returns
   */
  const updateProvenanceData = () => {
    if (!currentProvenanceRecord) return;

    const newProvenances = currentProperty.blpuProvenances.map(
      (x) => [currentProvenanceRecord].find((provenance) => provenance.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      newProvenances,
      currentProperty.blpuAppCrossRefs,
      currentProperty.classifications,
      currentProperty.organisations,
      currentProperty.successorCrossRefs,
      currentProperty.blpuNotes,
      "provenance"
    );
  };

  /**
   * Event to update the cross reference record with new data.
   *
   * @returns
   */
  const updateCrossRefData = () => {
    if (!currentProperty.blpuAppCrossRefs) return;

    const newCrossRefs = currentProperty.blpuAppCrossRefs.map(
      (x) => [currentProperty.blpuAppCrossRefs].find((xRef) => xRef.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      currentProperty.blpuProvenances,
      newCrossRefs,
      currentProperty.classifications,
      currentProperty.organisations,
      currentProperty.successorCrossRefs,
      currentProperty.blpuNotes,
      "appCrossRef"
    );
  };

  /**
   * Event to update the classification record with new data.
   *
   * @returns
   */
  const updateClassificationData = () => {
    if (!currentProperty.classifications) return;

    const newClassifications = currentProperty.classifications.map(
      (x) => [currentProperty.classification].find((classification) => classification.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      currentProperty.blpuProvenances,
      currentProperty.blpuAppCrossRefs,
      newClassifications,
      currentProperty.organisations,
      currentProperty.successorCrossRefs,
      currentProperty.blpuNotes,
      "classification"
    );
  };

  /**
   * Event to update the organisation record with new data.
   *
   * @returns
   */
  const updateOrganisationData = () => {
    if (!currentProperty.organisations) return;

    const newOrganisations = currentProperty.organisations.map(
      (x) => [currentProperty.organisation].find((organisation) => organisation.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      currentProperty.blpuProvenances,
      currentProperty.blpuAppCrossRefs,
      currentProperty.classifications,
      newOrganisations,
      currentProperty.successorCrossRefs,
      currentProperty.blpuNotes,
      "classification"
    );
  };

  /**
   * Event to update the lpi record with new data.
   *
   * @returns
   */
  const updateLpiData = () => {
    if (!currentLpiRecord) return;

    let newLpis = null;

    if (settingsContext.isWelsh) {
      const secondLanguage = currentLpiRecord.language === "ENG" ? "CYM" : "ENG";

      let secondLpi = null;
      if (currentLpiRecord.dualLanguageLink === 0) {
        const bilingualId = getBilingualSource(lookupContext);
        const linkXRef = currentProperty.blpuAppCrossRefs.find(
          (x) => x.sourceId === bilingualId && x.crossReference.includes(currentLpiRecord.lpiKey)
        );
        secondLpi = currentProperty.lpis.find(
          (x) => x.lpiKey === linkXRef.crossReference.replace(currentLpiRecord.lpiKey, "")
        );
      } else {
        secondLpi = currentProperty.lpis.find(
          (x) => x.dualLanguageLink === currentLpiRecord.dualLanguageLink && x.language === secondLanguage
        );
      }
      const secondPostTown = lookupContext.currentLookups.postTowns.find(
        (x) => x.linkedRef === currentLpiRecord.postTownRef && x.language === secondLpi.language
      );
      const newSecondLpi = {
        level: currentLpiRecord.level,
        postalAddress: currentLpiRecord.postalAddress,
        custodianOne: secondLpi.custodianOne,
        custodianTwo: secondLpi.custodianTwo,
        canKey: secondLpi.canKey,
        pkId: secondLpi.pkId,
        changeType: secondLpi.changeType,
        uprn: secondLpi.uprn,
        lpiKey: secondLpi.lpiKey,
        language: secondLpi.language,
        logicalStatus: currentLpiRecord.logicalStatus,
        startDate: currentLpiRecord.startDate,
        endDate: currentLpiRecord.endDate,
        entryDate: secondLpi.entryDate,
        lastUpdateDate: secondLpi.lastUpdateDate,
        saoStartNumber:
          secondLpi && secondLpi.saoStartNumber ? secondLpi.saoStartNumber : currentLpiRecord.saoStartNumber,
        saoStartSuffix:
          secondLpi && secondLpi.saoStartSuffix ? secondLpi.saoStartSuffix : currentLpiRecord.saoStartSuffix,
        saoEndNumber: secondLpi && secondLpi.saoEndNumber ? secondLpi.saoEndNumber : currentLpiRecord.saoEndNumber,
        saoEndSuffix: secondLpi && secondLpi.saoEndSuffix ? secondLpi.saoEndSuffix : currentLpiRecord.saoEndSuffix,
        saoText: secondLpi && secondLpi.saoText ? secondLpi.saoText : currentLpiRecord.saoText,
        paoStartNumber:
          secondLpi && secondLpi.paoStartNumber ? secondLpi.paoStartNumber : currentLpiRecord.paoStartNumber,
        paoStartSuffix:
          secondLpi && secondLpi.paoStartSuffix ? secondLpi.paoStartSuffix : currentLpiRecord.paoStartSuffix,
        paoEndNumber: secondLpi && secondLpi.paoEndNumber ? secondLpi.paoEndNumber : currentLpiRecord.paoEndNumber,
        paoEndSuffix: secondLpi && secondLpi.paoEndSuffix ? secondLpi.paoEndSuffix : currentLpiRecord.paoEndSuffix,
        paoText: secondLpi && secondLpi.paoText ? secondLpi.paoText : currentLpiRecord.paoText,
        usrn: currentLpiRecord.usrn,
        postcodeRef: currentLpiRecord.postcodeRef,
        postTownRef: secondPostTown ? secondPostTown.postTownRef : null,
        officialFlag: currentLpiRecord.officialFlag,
        neverExport: currentLpiRecord.neverExport,
        address: secondLpi.address,
        postTown: secondPostTown ? secondPostTown.postTown : null,
        postcode: currentLpiRecord.postcode,
        lastUpdated: secondLpi.lastUpdated,
        lastUser: secondLpi.lastUser,
        dualLanguageLink: secondLpi.dualLanguageLink,
      };

      newLpis = currentProperty.lpis.map(
        (x) =>
          [currentLpiRecord].find((lpi) => lpi.pkId === x.pkId) ||
          [newSecondLpi].find((lpi) => lpi.pkId === x.pkId) ||
          x
      );
    } else {
      newLpis = currentProperty.lpis.map((x) => [currentLpiRecord].find((lpi) => lpi.pkId === x.pkId) || x);
    }

    if (newLpis && newLpis.length > 0) {
      setAssociatedPropertyDataAndClear(
        newLpis,
        currentProperty.blpuProvenances,
        currentProperty.blpuAppCrossRefs,
        currentProperty.classifications,
        currentProperty.organisations,
        currentProperty.successorCrossRefs,
        currentProperty.blpuNotes,
        "lpi"
      );
    }
  };

  /**
   * Event to update the successor cross reference record with new data.
   *
   * @returns
   */
  const updateSuccessorCrossRefData = () => {
    if (!currentSuccessorCrossRefRecord) return;

    const newSuccessorCrossRefs = currentProperty.successorCrossRefs.map(
      (x) => [currentSuccessorCrossRefRecord].find((successorCrossRef) => successorCrossRef.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      currentProperty.blpuProvenances,
      currentProperty.blpuAppCrossRefs,
      currentProperty.classifications,
      currentProperty.organisations,
      newSuccessorCrossRefs,
      currentProperty.blpuNotes,
      "successorCrossRef"
    );
  };

  /**
   * Event to update the note (property) record with new data.
   *
   * @returns
   */
  const updatePropertyNoteData = () => {
    if (!currentPropertyNoteRecord) return;

    const newNotes = currentProperty.blpuNotes.map(
      (x) => [currentPropertyNoteRecord].find((xRef) => xRef.pkId === x.pkId) || x
    );

    setAssociatedPropertyDataAndClear(
      currentProperty.lpis,
      currentProperty.blpuProvenances,
      currentProperty.blpuAppCrossRefs,
      currentProperty.classifications,
      currentProperty.organisations,
      currentProperty.successorCrossRefs,
      newNotes,
      "propertyNote"
    );
  };

  /**
   * Event to handle when an error is clicked.
   *
   * @param {number} type The record type of the error clicked.
   * @param {object} error The error object.
   */
  const handleErrorClick = (type, error) => {
    if (
      sourceStreet &&
      (type !== streetContext.currentRecord.type || (type !== 11 && error.index !== streetContext.currentRecord.index))
    ) {
      const streetChanged =
        streetContext.currentStreet.newStreet ||
        (streetContext.currentRecord.type === 15 &&
          currentDescriptorRecord &&
          !ObjectComparison(
            sourceStreet.streetDescriptors.find((x) => x.pkId === currentDescriptorRecord.pkId),
            currentDescriptorRecord,
            streetDescriptorKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 13 &&
          currentEsuRecord &&
          !ObjectComparison(
            sourceStreet.esus.find((x) => x.pkId === currentEsuRecord.pkId),
            currentEsuRecord,
            esuKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 17 &&
          currentHighwayDedicationRecord &&
          !ObjectComparison(
            sourceStreet.esus
              .find((x) => x.esuId === currentHighwayDedicationRecord.esuId)
              .highwayDedications.find((x) => x.pkId === currentHighwayDedicationRecord.pkId),
            currentHighwayDedicationRecord,
            highwayDedicationKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 16 &&
          currentOneWayExemptionRecord &&
          !ObjectComparison(
            sourceStreet.esus
              .find((x) => x.esuId === currentOneWayExemptionRecord.esuId)
              .oneWayExemptions.find((x) => x.pkId === currentOneWayExemptionRecord.pkId),
            currentOneWayExemptionRecord,
            oneWayExemptionKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 51 &&
          currentMaintenanceResponsibilityRecord &&
          !ObjectComparison(
            sourceStreet.maintenanceResponsibilities.find(
              (x) => x.pkId === currentMaintenanceResponsibilityRecord.pkId
            ),
            currentMaintenanceResponsibilityRecord,
            maintenanceResponsibilityKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 52 &&
          currentReinstatementCategoryRecord &&
          !ObjectComparison(
            sourceStreet.reinstatementCategories.find((x) => x.pkId === currentReinstatementCategoryRecord.pkId),
            currentReinstatementCategoryRecord,
            reinstatementCategoryKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 53 &&
          currentOSSpecialDesignationRecord &&
          !ObjectComparison(
            sourceStreet.specialDesignations.find((x) => x.pkId === currentOSSpecialDesignationRecord.pkId),
            currentOSSpecialDesignationRecord,
            specialDesignationKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 61 &&
          currentInterestRecord &&
          !ObjectComparison(
            sourceStreet.interests.find((x) => x.pkId === currentInterestRecord.pkId),
            currentInterestRecord,
            interestKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 62 &&
          currentConstructionRecord &&
          !ObjectComparison(
            sourceStreet.constructions.find((x) => x.pkId === currentConstructionRecord.pkId),
            currentConstructionRecord,
            constructionKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 63 &&
          currentSpecialDesignationRecord &&
          !ObjectComparison(
            sourceStreet.specialDesignations.find((x) => x.pkId === currentSpecialDesignationRecord.pkId),
            currentSpecialDesignationRecord,
            specialDesignationKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 64 &&
          currentHWWRecord &&
          !ObjectComparison(
            sourceStreet.heightWidthWeights.find((x) => x.pkId === currentHWWRecord.pkId),
            currentHWWRecord,
            heightWidthWeightKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 66 &&
          currentPRoWRecord &&
          !ObjectComparison(
            sourceStreet.publicRightOfWays.find((x) => x.pkId === currentPRoWRecord.pkId),
            currentPRoWRecord,
            publicRightOfWayKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 72 &&
          currentStreetNoteRecord &&
          !ObjectComparison(
            sourceStreet.streetNotes.find((x) => x.pkId === currentStreetNoteRecord.pkId),
            currentStreetNoteRecord,
            noteKeysToIgnore
          )) ||
        (streetContext.currentRecord.type === 11 &&
          currentStreet &&
          !StreetComparison(sourceStreet, currentStreet, hasASD));

      if (streetChanged) {
        confirmDialog(true)
          .then((result) => {
            if (result === "save") {
              doKeepChanges();
            }
            doErrorClick(type, error);
          })
          .catch(() => {});
      } else {
        doErrorClick(type, error);
      }
    } else if (
      sourceProperty &&
      (type !== propertyContext.currentRecord.type ||
        (type !== 21 && error.index !== propertyContext.currentRecord.index))
    ) {
      const propertyChanged =
        propertyContext.currentProperty.newProperty ||
        (propertyContext.currentRecord.type === 24 &&
          currentLpiRecord &&
          !ObjectComparison(
            sourceProperty.lpis.find((x) => x.pkId === currentLpiRecord.pkId),
            currentLpiRecord,
            lpiKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 23 &&
          currentAppCrossRefRecord &&
          !ObjectComparison(
            sourceProperty.blpuAppCrossRefs.find((x) => x.pkId === currentAppCrossRefRecord.pkId),
            currentAppCrossRefRecord,
            blpuAppCrossRefKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 22 &&
          currentProvenanceRecord &&
          !ObjectComparison(
            sourceProperty.blpuProvenances.find((x) => x.pkId === currentProvenanceRecord.pkId),
            currentProvenanceRecord,
            provenanceKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 32 &&
          currentClassificationRecord &&
          !ObjectComparison(
            sourceProperty.classifications.find((x) => x.pkId === currentClassificationRecord.pkId),
            currentClassificationRecord,
            classificationKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 31 &&
          currentOrganisationRecord &&
          !ObjectComparison(
            sourceProperty.organisations.find((x) => x.pkId === currentOrganisationRecord.pkId),
            currentOrganisationRecord,
            organisationKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 30 &&
          currentSuccessorCrossRefRecord &&
          !ObjectComparison(
            sourceProperty.successorCrossRefs.find((x) => x.pkId === currentSuccessorCrossRefRecord.pkId),
            currentSuccessorCrossRefRecord,
            successorCrossRefKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 71 &&
          currentPropertyNoteRecord &&
          !ObjectComparison(
            sourceProperty.blpuNotes.find((x) => x.pkId === currentPropertyNoteRecord.pkId),
            currentPropertyNoteRecord,
            noteKeysToIgnore
          )) ||
        (propertyContext.currentRecord.type === 21 &&
          currentProperty &&
          !PropertyComparison(sourceProperty, currentProperty));

      if (propertyChanged) {
        confirmDialog(true)
          .then((result) => {
            if (result === "save") {
              if (propertyContext.validateData()) {
                doKeepChanges();
                doErrorClick(type, error);
              } else {
                setValidationErrorOpen(true);
              }
            } else {
              doErrorClick(type, error);
            }
          })
          .catch(() => {});
      } else {
        doErrorClick(type, error);
      }
    } else {
      doErrorClick(type, error);
    }
  };

  /**
   * Event to handle when the dialog is closing.
   *
   * @param {object} event The event object.
   * @param {string} reason The reason why the dialog is closing.
   * @returns
   */
  const handleValidationErrorClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setValidationErrorOpen(false);
  };

  /**
   * A method to return the list item for an error.
   *
   * @param {Number} type The type of record
   * @param {Object} error The error object
   * @param {String} errorText The error text
   * @param {Number} index The index for the error map
   * @param {Number} indx The index for the error text map
   * @returns {JSX.Element} The ListItem for the error.
   */
  const getErrorListItem = (type, error, errorText, index, indx) => {
    return (
      <ListItem dense disablePadding key={`${type}-error-${index}-${indx}`}>
        <ListItemText
          primary={
            <Link
              component="button"
              variant="caption"
              align="left"
              color={adsRed}
              key={`${type}|${index}|${indx}`}
              onClick={() => handleErrorClick(type, error)}
              sx={getIssueStyle}
              underline="hover"
            >
              {errorText.replace("¬", ",")}
            </Link>
          }
        />
      </ListItem>
    );
  };

  /**
   * Event to handle when the message dialog is closed.
   *
   * @param {String} action The action chosen from the message dialog.
   */
  const handleMessageDialogClose = (action) => {
    setMessageOpen(false);
  };

  useEffect(() => {
    setHasASD(userContext.currentUser && userContext.currentUser.hasASD);
  }, [userContext]);

  return (
    <Fragment>
      <Box
        sx={{
          position: "absolute",
          width: "25ch",
          maxHeight: "40vh",
          overflowY: "auto",
          overflowX: "auto",
          left: theme.spacing(79.25),
          top: theme.spacing(2.5),
          borderWidth: "1px",
          borderColor: adsLightGreyB,
          backgroundColor: adsOffWhite,
          boxShadow: `4px 4px 7px ${adsLightGreyA50}`,
          borderRadius: "3px",
        }}
      >
        <Grid container direction="column" id="issue-list-grid" sx={{ pt: "2px", pl: "12px", pr: "12px", pb: "24px" }}>
          <Grid>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography
                variant="subtitle1"
                display="inline-flex"
                sx={{
                  mt: theme.spacing(0.3),
                  pl: theme.spacing(1.1),
                  fontWeight: 600,
                  fontSize: "18px",
                  color: adsDarkGrey,
                }}
              >
                Issue(s)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Tooltip title="Copy errors to clipboard" placement="top">
                  <IconButton onClick={handleCopyClick} size="small">
                    <CopyIcon sx={ActionIconStyle()} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" placement="top">
                  <IconButton onClick={handleCloseClick} size="small">
                    <CloseIcon sx={ActionIconStyle()} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Grid>
          <Grid>
            <List component="div" disablePadding>
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.street.length > 0 &&
                streetContext.currentErrors.street.map((streetError, index) => {
                  return streetError.errors.map((streetErrorText, indx) => {
                    return getErrorListItem(11, streetError, streetErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.descriptor.length > 0 &&
                streetContext.currentErrors.descriptor.map((descriptorError, index) => {
                  return descriptorError.errors.map((descriptorErrorText, indx) => {
                    return getErrorListItem(15, descriptorError, descriptorErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.esu.length > 0 &&
                streetContext.currentErrors.esu.map((esuError, index) => {
                  return esuError.errors.map((esuErrorText, indx) => {
                    return getErrorListItem(13, esuError, esuErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.highwayDedication.length > 0 &&
                streetContext.currentErrors.highwayDedication.map((highwayDedicationError, index) => {
                  return highwayDedicationError.errors.map((highwayDedicationErrorText, indx) => {
                    return getErrorListItem(17, highwayDedicationError, highwayDedicationErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.oneWayExemption.length > 0 &&
                streetContext.currentErrors.oneWayExemption.map((oneWayExemptionError, index) => {
                  return oneWayExemptionError.errors.map((oneWayExemptionErrorText, indx) => {
                    return getErrorListItem(16, oneWayExemptionError, oneWayExemptionErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.successorCrossRef.length > 0 &&
                streetContext.currentErrors.successorCrossRef.map((successorCrossRefError, index) => {
                  return successorCrossRefError.errors.map((successorCrossRefErrorText, indx) => {
                    return getErrorListItem(30, successorCrossRefError, successorCrossRefErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.maintenanceResponsibility.length > 0 &&
                streetContext.currentErrors.maintenanceResponsibility.map((maintenanceResponsibilityError, index) => {
                  return maintenanceResponsibilityError.errors.map((maintenanceResponsibilityErrorText, indx) => {
                    return getErrorListItem(
                      51,
                      maintenanceResponsibilityError,
                      maintenanceResponsibilityErrorText,
                      index,
                      indx
                    );
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.reinstatementCategory.length > 0 &&
                streetContext.currentErrors.reinstatementCategory.map((reinstatementCategoryError, index) => {
                  return reinstatementCategoryError.errors.map((reinstatementCategoryErrorText, indx) => {
                    return getErrorListItem(
                      52,
                      reinstatementCategoryError,
                      reinstatementCategoryErrorText,
                      index,
                      indx
                    );
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.osSpecialDesignation.length > 0 &&
                streetContext.currentErrors.osSpecialDesignation.map((osSpecialDesignationError, index) => {
                  return osSpecialDesignationError.errors.map((osSpecialDesignationErrorText, indx) => {
                    return getErrorListItem(53, osSpecialDesignationError, osSpecialDesignationErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.interest.length > 0 &&
                streetContext.currentErrors.interest.map((interestError, index) => {
                  return interestError.errors.map((interestErrorText, indx) => {
                    return getErrorListItem(61, interestError, interestErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.construction.length > 0 &&
                streetContext.currentErrors.construction.map((constructionError, index) => {
                  return constructionError.errors.map((constructionErrorText, indx) => {
                    return getErrorListItem(62, constructionError, constructionErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.specialDesignation.length > 0 &&
                streetContext.currentErrors.specialDesignation.map((specialDesignationError, index) => {
                  return specialDesignationError.errors.map((specialDesignationErrorText, indx) => {
                    return getErrorListItem(63, specialDesignationError, specialDesignationErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.heightWidthWeight.length > 0 &&
                streetContext.currentErrors.heightWidthWeight.map((heightWidthWeightError, index) => {
                  return heightWidthWeightError.errors.map((heightWidthWeightErrorText, indx) => {
                    return getErrorListItem(64, heightWidthWeightError, heightWidthWeightErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.publicRightOfWay.length > 0 &&
                streetContext.currentErrors.publicRightOfWay.map((publicRightOfWayError, index) => {
                  return publicRightOfWayError.errors.map((publicRightOfWayErrorText, indx) => {
                    return getErrorListItem(66, publicRightOfWayError, publicRightOfWayErrorText, index, indx);
                  });
                })}
              {streetContext.currentStreetHasErrors &&
                streetContext.currentErrors.note.length > 0 &&
                streetContext.currentErrors.note.map((noteError, index) => {
                  return noteError.errors.map((noteErrorText, indx) => {
                    return getErrorListItem(72, noteError, noteErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.blpu.length > 0 &&
                propertyContext.currentErrors.blpu.map((blpuError, index) => {
                  return blpuError.errors.map((blpuErrorText, indx) => {
                    return getErrorListItem(21, blpuError, blpuErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.lpi.length > 0 &&
                propertyContext.currentErrors.lpi.map((lpiError, index) => {
                  return lpiError.errors.map((lpiErrorText, indx) => {
                    return getErrorListItem(24, lpiError, lpiErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.provenance.length > 0 &&
                propertyContext.currentErrors.provenance.map((provenanceError, index) => {
                  return provenanceError.errors.map((provenanceErrorText, indx) => {
                    return getErrorListItem(22, provenanceError, provenanceErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.crossRef.length > 0 &&
                propertyContext.currentErrors.crossRef.map((crossRefError, index) => {
                  return crossRefError.errors.map((crossRefErrorText, indx) => {
                    return getErrorListItem(23, crossRefError, crossRefErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.successorCrossRef.length > 0 &&
                propertyContext.currentErrors.successorCrossRef.map((successorCrossRefError, index) => {
                  return successorCrossRefError.errors.map((successorCrossRefErrorText, indx) => {
                    return getErrorListItem(30, successorCrossRefError, successorCrossRefErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.organisation.length > 0 &&
                propertyContext.currentErrors.organisation.map((organisationError, index) => {
                  return organisationError.errors.map((organisationErrorText, indx) => {
                    return getErrorListItem(31, organisationError, organisationErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.classification.length > 0 &&
                propertyContext.currentErrors.classification.map((classificationError, index) => {
                  return classificationError.errors.map((classificationErrorText, indx) => {
                    return getErrorListItem(32, classificationError, classificationErrorText, index, indx);
                  });
                })}
              {propertyContext.currentPropertyHasErrors &&
                propertyContext.currentErrors.note.length > 0 &&
                propertyContext.currentErrors.note.map((noteError, index) => {
                  return noteError.errors.map((noteErrorText, indx) => {
                    return getErrorListItem(71, noteError, noteErrorText, index, indx);
                  });
                })}
            </List>
          </Grid>
        </Grid>
      </Box>
      <div>
        <Snackbar
          open={validationErrorOpen}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleValidationErrorClose}
        >
          <Alert
            sx={GetAlertStyle(false)}
            icon={GetAlertIcon(false)}
            onClose={handleValidationErrorClose}
            severity={GetAlertSeverity(false)}
            elevation={6}
            variant="filled"
          >
            {"Failed to validate the record."}
          </Alert>
        </Snackbar>
        <MessageDialog isOpen={messageOpen} variant="copiedIssues" onClose={handleMessageDialogClose} />
      </div>
    </Fragment>
  );
}

export default ADSErrorList;
